import React, { useContext, useState, useEffect, useRef } from "react";
import { Store } from "../types/store";
import { useAuth } from "../Auth/AuthProvider";
import useSWRImmutable from "swr/immutable";
import { fetcherUserStore } from "../utils/fetcher";
import LoadingPage from "../components/LoadingPage";
import { userTrack, event } from "../utils/gtag";
import { useTranslation } from "react-i18next";
import ForbiddenPage from "../components/ForbiddenPage";

type StoreContextType = {
  selectStore: (storeKey: string | null) => void;
  selectedStore: Store | null;
  stores: Store[];
};

const StoreContext = React.createContext<StoreContextType>({
  selectStore: () => {},
  stores: [],
  selectedStore: null,
});

type StoreProviderProps = {
  children: React.ReactNode;
};

export const StoreProvider: React.FunctionComponent<StoreProviderProps> = ({
  children,
}: StoreProviderProps) => {
  const { user, isAuthenticated, logout, isLoading } = useAuth();
  const {t} = useTranslation();
  const [stores, setStores] = useState<Array<Store>>([]);
  const [selectedStore, setSelectedStore] = useState<Store | null>(
    stores?.[0] ?? null
  );
  const [indexStore, setIndexStore] = useState<number>();
  // ref to keep previous selected store id to avoid duplicate tracking calls
  const prevStoreIdRef = useRef<string | undefined>(undefined);
  const hasCockpitAccess = (user?.fr_cockpit_360 as string[])?.length > 0;

  const { data: userStore, error } = useSWRImmutable<Store[]>(
    () =>
      user && !indexStore && hasCockpitAccess
        ? [
            `/stores/user`,
            user?.id,
            (user?.fr_cockpit_360 as string[])?.[0],
          ]
        : null,
    ([url, auditTrackingId, role]) =>
      fetcherUserStore(
        url,
        auditTrackingId as string,
        role as string
      )
  );

  useEffect(() => {
    // Only send userTrack when the selected store actually changes (by id).
    // Use a ref to remember the previous store id and avoid duplicate calls
    const prevStoreId = prevStoreIdRef.current;
    const currentId = selectedStore?.storeAnabelKey;
    if (!currentId) return;
    if (prevStoreId === currentId) return;
    prevStoreIdRef.current = currentId;
    userTrack({
      store_name: selectedStore.storeName,
      store_type: selectedStore.storeType,
      store_id: currentId,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStore?.storeAnabelKey]);

  useEffect(() => {
    if(user?.roles) {
        userTrack({
            user_type: user?.roles?.join(','),
        });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);


    useEffect(() => {
    if (userStore === undefined) return;
    const indexLocal =
      (JSON.parse(localStorage.getItem("indexStoreData")!) as number) || 0;

    setSelectedStore(userStore?.[indexLocal] || null);
    setStores(userStore as Store[]);
    setIndexStore(indexLocal);
    if (userStore?.[indexLocal]) {
      // initial tracking for the store and user
      userTrack({
        store_name: userStore?.[indexLocal]?.storeName,
        store_type: userStore?.[indexLocal]?.storeType,
        store_id: userStore?.[indexLocal]?.storeAnabelKey,
        user_type: user?.roles?.join(","),
      });
      // avoid the selectedStore effect from firing again for the same store
      prevStoreIdRef.current = userStore?.[indexLocal]?.storeAnabelKey;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userStore]);

  const selectStore = (storeKey: string | null) => {

    const index = stores.findIndex(
      (store: Store) => store.storeKey === storeKey
    );
    if (index !== -1) {
      setIndexStore(index);
      setSelectedStore(stores[index] ?? null);
      const current = stores[index];
      if (current) {
        event({
          action: "Select_store",
          value: current?.storeName,
        });
        event({
          action: "Type_store",
          value: current?.storeType,
        });
      }
      localStorage.setItem("indexStoreData", JSON.stringify(index));
      return index;
    }
  };

  if (error || (!isLoading && isAuthenticated && !hasCockpitAccess) || (userStore && userStore.length === 0)) {
    return <ForbiddenPage logout={logout} />;
  }

  if (isAuthenticated && userStore === undefined && !selectedStore && hasCockpitAccess) {
    return <LoadingPage message={t('common.chargement')} />;
  }

  return (
    <StoreContext.Provider value={{ selectStore, stores, selectedStore }}>
      {children}
    </StoreContext.Provider>
  );
};

export const useStores: () => StoreContextType = () => useContext(StoreContext);
